import { CurrentUserDTO, UserType } from "@bvhi/backend-api";
import { styled } from "@bvhi/ui-base";
import { Outlet } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { UpdatedTermsOfServiceConsentGate } from "../../../components/agb-2026-consent-gate";
import { UpdatedTermsOfServiceConsentDashboardWarningBanner } from "../../../components/agb-2026-dashboard-warning-banner";
import { BVHIAppBar } from "../../../components/bvhi-app-bar";
import { BVHIBounceLoader } from "../../../components/bvhi-bounce-loader";
import { BVHIDrawer } from "../../../components/bvhi-drawer";
import { BVHIFooter } from "../../../components/bvhi-footer";
import { NavigationBar } from "../../../components/navigation-bar";
import { MUI_DRAWER_WIDTH } from "../../../config/constants";
import { AppNavigationConfig } from "../../../config/navigation";
import { useAuthContext } from "../../../context/use-auth-context";
import { AcceptTermsOfServiceDialog } from "../../../dialogs/accept-terms-of-service-dialog";
import { useAuthentication } from "../../../hooks/use-authentication";

const GridContainer = styled("div")({
  height: "100svh",
  width: "100svw",
  overflowX: "hidden",
  maxWidth: "100%",
  display: "grid",
  gridTemplateColumns: `${MUI_DRAWER_WIDTH}px 1fr`,
  gridTemplateRows: "64px 1fr 64px",
  gridTemplateAreas: `"appbar appbar" "drawer main" "footer footer"`,
  alignItems: "stretch",
  justifyItems: "stretch",
  alignContent: "stretch",
  justifyContent: "stretch",
});

const GridAppbarItem = styled("div")({
  gridArea: "appbar",
});

const GridDrawerItem = styled("div")(() => ({
  display: "flex",
  gridArea: "drawer",
}));

const GridMainItem = styled("div")(({ theme }) => ({
  gridArea: "main",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const GridFooterItem = styled("div")({
  gridArea: "footer",
});

function isUpdatedTermsOfServiceConsentRequired(currentUser: CurrentUserDTO | undefined): boolean {
  if (!currentUser) return false;
  if (currentUser.type !== UserType.AUDIOLOGIST && currentUser.type !== UserType.ERP_SUPPLIER) return false;
  return currentUser.updatedTermsOfServiceConsentRequired;
}

export const MainLayoutView = () => {
  const { isAuthenticated } = useAuthentication();
  const { currentUser } = useAuthContext();
  const [updatedTermsOfServiceConsentGateDismissedWithoutConsent, setUpdatedTermsOfServiceConsentGateDismissedWithoutConsent] =
    useState(false);

  useEffect(() => {
    void isAuthenticated();
  }, [isAuthenticated]);

  const showUpdatedTermsOfServiceConsentGate =
    currentUser && isUpdatedTermsOfServiceConsentRequired(currentUser) && !updatedTermsOfServiceConsentGateDismissedWithoutConsent;
  const showUpdatedTermsOfServiceConsentDashboardWarningBanner =
    currentUser && isUpdatedTermsOfServiceConsentRequired(currentUser) && updatedTermsOfServiceConsentGateDismissedWithoutConsent;

  if (showUpdatedTermsOfServiceConsentGate) {
    return (
      <UpdatedTermsOfServiceConsentGate onContinueWithoutConsent={() => setUpdatedTermsOfServiceConsentGateDismissedWithoutConsent(true)} />
    );
  }

  return (
    <>
      <GridContainer>
        <GridAppbarItem>
          <BVHIAppBar />
        </GridAppbarItem>
        <GridDrawerItem>
          <BVHIDrawer navigationConfig={AppNavigationConfig} />
        </GridDrawerItem>
        <GridMainItem>
          {showUpdatedTermsOfServiceConsentDashboardWarningBanner && <UpdatedTermsOfServiceConsentDashboardWarningBanner />}
          <Suspense
            fallback={
              <>
                <NavigationBar showBreadcrumbs={false} />
                <BVHIBounceLoader position="relative" />
              </>
            }
          >
            <Outlet />
          </Suspense>
        </GridMainItem>
        <GridFooterItem>
          <BVHIFooter navigationConfig={AppNavigationConfig} />
        </GridFooterItem>
      </GridContainer>

      <AcceptTermsOfServiceDialog open={!currentUser?.termsOfServiceConsent} />
    </>
  );
};
